/**
 * The application's only dialog (tasks 9.8, 9.9, 9.11).
 *
 * Modal for real: focus moves in on open, Tab cycles inside, Escape and the
 * backdrop dismiss, and focus returns to whatever opened it. How to Play uses
 * it as an overlay mid-mission, which is why closing restores the exact prior
 * state rather than navigating.
 */
import { useCallback, useEffect, useId, useRef, type JSX, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  /** Rendered under the title; also the dialog's accessible description. */
  readonly description?: string | undefined;
  readonly children?: ReactNode | undefined;
  readonly footer?: ReactNode | undefined;
  readonly size?: 'sm' | 'lg' | undefined;
  readonly testId?: string | undefined;
  onClose(): void;
}

export function Modal({
  open,
  title,
  description,
  children,
  footer,
  size = 'sm',
  testId = 'modal',
  onClose,
}: ModalProps): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const focusables = useCallback(
    (): HTMLElement[] =>
      panel.current ? [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)] : [],
    [],
  );

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    (focusables()[0] ?? panel.current)?.focus();
    return () => {
      returnTo.current?.focus();
    };
  }, [open, focusables]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, onClose, focusables]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        data-testid={`${testId}-backdrop`}
        className="absolute inset-0 cursor-default bg-black/50"
        onClick={onClose}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        data-testid={testId}
        tabIndex={-1}
        className={`pb-safe relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-line bg-surface shadow-card sm:rounded-2xl ${
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-md'
        }`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line p-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-ink">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
                {description}
              </p>
            ) : null}
          </div>
          <Button variant="ghost" onClick={onClose} data-testid={`${testId}-close`}>
            Close
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line p-4">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  /** Names exactly what is about to be lost. Required for destructive actions. */
  readonly consequence: string;
  readonly confirmLabel: string;
  readonly testId?: string | undefined;
  onConfirm(): void;
  onCancel(): void;
}

/** A confirmation that states the consequence rather than asking "are you sure?". */
export function ConfirmDialog({
  open,
  title,
  consequence,
  confirmLabel,
  testId = 'confirm',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  return (
    <Modal
      open={open}
      title={title}
      testId={testId}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} data-testid={`${testId}-cancel`}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} data-testid={`${testId}-confirm`}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink">{consequence}</p>
    </Modal>
  );
}
